import { useSyncExternalStore } from "react";

/**
 * Whether the request reached an answer at all.
 *
 * This is the one question the portal never asked. `app/auth/signInFailure.ts` asked it for the
 * sign-in form on 2026-09-02, after a staging build pointed at `http://127.0.0.1:54321` reported
 * every failed sign-in as a wrong password, and the answer stayed inside that one file. Everywhere
 * else a dropped connection arrived at `toPortalFailure`, matched no `PORTAL_` code and became
 * `PORTAL_UNEXPECTED`, whose sentence is "Något gick fel. Försök igen." A person reading that tries
 * the same thing again over the same dead connection.
 *
 * plan.md section 5.8 is the requirement: "Offline actions show a clear reconnect state rather than
 * pretending to have been submitted."
 *
 * The shapes below are the ones a browser and supabase-js actually produce, and they are the same
 * three `classifySignInFailure` matches, which now reads them from here rather than keeping a second
 * copy:
 *
 * - an unwrapped `fetch` rejection is a `TypeError` with no status at all
 * - supabase-js wraps a transport failure as `AuthRetryableFetchError` with status 0
 * - a PostgREST call that never left the machine arrives as a plain error whose message names the
 *   fetch
 *
 * What it deliberately does not match is a status the server chose. A 500 is an answer. Calling an
 * answer a network failure would replace a true sentence about the server with a false sentence
 * about the network, which is the same class of mistake in the other direction.
 */
export function isTransportFailure(error: unknown): boolean {
  const shape =
    typeof error === "object" && error !== null
      ? (error as { status?: unknown; name?: unknown; message?: unknown })
      : {};

  const status = typeof shape.status === "number" ? shape.status : null;
  const name = typeof shape.name === "string" ? shape.name : "";
  const message = typeof shape.message === "string" ? shape.message : "";

  if (status !== null && status !== 0) {
    return false;
  }

  return (
    status === 0 ||
    name === "AuthRetryableFetchError" ||
    name === "TypeError" ||
    /fetch|network|failed to fetch/i.test(message)
  );
}

/**
 * What the browser itself claims about the connection.
 *
 * `navigator.onLine` is worth exactly what it says and no more: false is reliable, true only means
 * the machine has an interface with a route on it. So it is used one way round. False settles the
 * question, and true settles nothing, which is why `isTransportFailure` still has to look at the
 * error after this returns false.
 *
 * A runtime without the property is treated as online, because refusing to work on the assumption
 * of an unknown is how the portal would end up showing a reconnect banner to somebody who is
 * connected.
 */
export function browserIsOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function subscribeToConnection(onChange: () => void): () => void {
  window.addEventListener("online", onChange);
  window.addEventListener("offline", onChange);

  return () => {
    window.removeEventListener("online", onChange);
    window.removeEventListener("offline", onChange);
  };
}

/**
 * The connection as a piece of state, so the shell can render it.
 *
 * `useSyncExternalStore` rather than `useState` plus an effect, because the connection is exactly
 * what that hook is for: state that lives outside React and changes without React being told. The
 * version written first here kept a `useState` seeded from `browserIsOffline` and re-read it inside
 * the effect, to cover the window between the first render and the listeners being attached, in
 * which no event is delivered and nothing would say the state had moved. That re-read is a
 * `setState` in an effect body, which the project's `react-hooks/set-state-in-effect` rule refuses,
 * and it refuses it correctly: the hook below closes the same window by reading the snapshot on
 * every render instead of copying it once.
 *
 * The third argument is the server snapshot. Nothing here is server rendered, and the value is
 * `true` rather than a read of `navigator`, because a runtime with no `navigator` is not a runtime
 * that can be offline in any sense a person would recognise.
 */
export function useConnectionStatus(): { online: boolean } {
  const online = useSyncExternalStore(
    subscribeToConnection,
    () => !browserIsOffline(),
    () => true
  );

  return { online };
}
